import { useLang } from 'hooks/lang';

import {
  getProjectAmountTaken,
  invoiceControlStatusConfig,
  InvoiceProjectByStatus,
} from 'entities';

import { Button, Card, Row, Table, TableColumn } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { CheckIcon, XIcon } from 'components/Icons';

import { formatCurrency, truncateText } from 'utils/text';
import { getFirst } from 'utils/array';

interface CardViewProps {
  controls: InvoiceProjectByStatus[];
  loading?: boolean;
}

export function InvoiceControlTable({ controls, loading }: CardViewProps) {
  const { t } = useLang();

  const status = getFirst(controls)?.status ?? '';

  const columnByStatus: Record<string, TableColumn<InvoiceProjectByStatus>> = {
    [invoiceControlStatusConfig.waitingInvoiceInfo.code]: {
      label: t('ready?'),
      property: 'readyToInvoice',
      filter: true,
      sort: true,
      className: 'text-center',
      render: ({ readyToInvoice }) => (
        <Row justifyContent="center">
          {readyToInvoice ? <CheckIcon color="success" /> : <XIcon />}
        </Row>
      ),
    },

    [invoiceControlStatusConfig.unassigned.code]: {
      hide: true,
      property: 'assignedAt',
    },
  };

  return (
    <Card>
      <Table
        loading={loading}
        items={controls}
        itemsPerPage={15}
        uniqueKey="assignedAt"
        columns={[
          {
            label: t('name'),
            search: true,
            sort: true,
            getTextValue: ({ project }) => project.name,
            render: ({ project }) => (
              <Button
                tooltipDisabled={project.name.length < 35}
                tooltipContent={project.name}
                to="projectProfile"
                params={{ projectId: project.id }}
                variant="link"
                color="gray"
              >
                {truncateText(project.name, 35)}
              </Button>
            ),
          },
          {
            label: t('client'),
            render: ({ project }) => project.clientName,
            filter: true,
          },
          {
            label: t('value'),
            render: ({ project }) => formatCurrency(project.total, project.currency),
            filter: true,
            sort: true,
            sorter: (a, b) => Number(a.project.total) - Number(b.project.total),
            getTextValue: ({ project }) => project.currency,
          },
          {
            label: t('payment'),
            render: ({ type }) => type ?? '?',
            filter: true,
          },

          columnByStatus[status] ?? {
            label: t('taken'),
            filter: true,
            sort: true,
            sorter: (a, b) => Number(getProjectAmountTaken(a)) - Number(getProjectAmountTaken(b)),
            getTextValue: ({ project }) => project.currency,

            render: (control) =>
              formatCurrency(getProjectAmountTaken(control), control.project.currency),
          },

          {
            label: t('ticket'),
            render: ({ project }) => project.ticket,
            search: true,
          },
          {
            label: t('type'),
            filter: true,
            sort: true,
            render: ({ project }) => t(project.type),
          },
          {
            label: t('owner'),
            filter: true,
            getTextValue: ({ owner }) => owner.name,
            render: ({ owner }) => (
              <UserAvatar user={owner} name nameClassName="text-sm" size={28} />
            ),
          },
        ]}
      />
    </Card>
  );
}
